"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { customerApi, CUSTOMER_AUTH_EVENT } from "@/lib/customer-api";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { localizeHref } from "@/lib/slug-paths";

/**
 * Authed-state overlay for the My Favotrip header entry.
 *
 * Bug 2 fix (Jeanne 2026-05-19/20): paired with
 * `AccountHeaderLinkAnonShell`. The shell renders the anonymous
 * "Inloggen" pill server-side (always visible to crawlers + anon
 * customers). On the client, this component fetches /customer/me; if
 * authed, it renders a "Hi {firstName}" pill AND hides the sibling
 * shell via the shared `data-account-header-mode` attribute on the
 * wrapping container (toggled below). If anonymous, it returns null —
 * leaving the shell as the only visible element.
 *
 * The DOM-state contract:
 *   data-account-header-mode="anon"   → shell visible, this hidden
 *   data-account-header-mode="authed" → shell hidden, this visible
 *
 * CSS rules live in globals.css under .account-header-link-shell /
 * .account-header-link-auth.
 */

function ProfileIconAuthed() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

// /account portal routes, incl. localized first segment (konto, compte) and
// an optional two-letter locale prefix. AccountShell does a real me() there.
const ACCOUNT_ROUTE_RE = /^\/(?:[a-z]{2}\/)?(?:account|konto|compte)(?:\/|$)/i;

export default function AccountHeaderLinkAuth({ variant = "desktop" }) {
  const pathname = usePathname();
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [me, setMe] = useState(null);

  useEffect(() => {
    let controller = null;
    const isAccountRoute = ACCOUNT_ROUTE_RE.test(pathname || "");
    const load = () => {
      if (controller) controller.abort();
      const c = new AbortController();
      controller = c;
      // Re-check auth on pathname change AND on the login event so the
      // header flips immediately after login / logout. The abort on
      // cleanup stops a stale fetch from applying its result.
      (async () => {
        // meQuiet is hint-gated (anonymous pageviews stay network-silent,
        // PR #876) and contractually never rejects, but the header must
        // not depend on that. On /account routes the hint can still be
        // missing right after a Google / magic-link login, so there we use
        // the real me() (a 401 there simply means anonymous).
        let profile = null;
        try {
          profile = isAccountRoute
            ? await customerApi.me({ signal: c.signal })
            : await customerApi.meQuiet();
        } catch {
          profile = null;
        }
        if (c.signal.aborted) return;
        setMe(profile);
      })();
    };
    load();
    window.addEventListener(CUSTOMER_AUTH_EVENT, load);
    return () => {
      window.removeEventListener(CUSTOMER_AUTH_EVENT, load);
      if (controller) controller.abort();
    };
  }, [pathname]);

  // Toggle the wrapping container's data attribute so the sibling
  // anonymous shell can hide via CSS when this component shows.
  useEffect(() => {
    const containers = document.querySelectorAll(
      `[data-account-header-wrap="${variant}"]`,
    );
    containers.forEach((el) => {
      el.setAttribute("data-account-header-mode", me ? "authed" : "anon");
    });
  }, [me, variant]);

  if (!me) {
    // Anonymous: don't render anything. The shell handles the visible UI.
    return null;
  }

  const accountLabel = t("nav.account", "Mijn Favotrip");
  const label = me.firstName ? `Hi ${me.firstName}` : accountLabel;
  const bookingsHref = localizeHref("/account/boekingen", locale);

  if (variant === "mobile") {
    return (
      <Link
        href={bookingsHref}
        aria-label={accountLabel}
        className="account-header-link-auth account-header-link-auth--mobile p-2 text-gray-500 transition-colors hover:text-blue-600"
        data-account-header-state="authed"
      >
        <ProfileIconAuthed />
      </Link>
    );
  }

  return (
    <Link
      href={bookingsHref}
      className="account-header-link-auth account-header-link-auth--desktop inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3.5 py-2 text-[14px] font-semibold text-gray-700 transition-colors hover:border-blue-200 hover:text-blue-600"
      data-account-header-state="authed"
    >
      <ProfileIconAuthed />
      <span className="max-w-[140px] truncate">{label}</span>
    </Link>
  );
}
